'use client';

import { getNatsClient, hasMinRole, normalizeRole, useKhalAuth, useSandboxStatus } from '@khal-os/sdk/app';
import type { SandboxState } from '@khal-os/sdk/app';
import { Spinner } from '@khal-os/ui';
import type { Role } from '@khal-os/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLaunchApp } from '../../hooks/useLaunchApp';
import { HIDDEN_APP_SLUGS, useDesktopStore } from '../../stores/desktop-store';
import type { DesktopEntry } from '../../types/desktop-entry';
import { AppCard, type StoreEntry } from './AppCard';
import { InstallFromUrlDialog, type InstallProgress } from './InstallFromUrlDialog';

/**
 * Response shape from `os.apps.store.list`.
 *
 * The handler in core (`src/lib/app-store-handlers.ts`) responds with
 * `{ items }` because the store can hold non-app entries (workflows,
 * skills, templates, …) discriminated by `itemType`. The legacy `apps`
 * field is accepted for backwards compatibility with older kernels that
 * still serialise the response as `{ apps }`.
 */
interface StoreCatalogResponse {
	items?: StoreEntry[];
	apps?: StoreEntry[];
	error?: string;
}

/** How many installed packs to surface in the hero / featured row.
 *  Decision #10: featured uses a heuristic (first installed packs) — no `featured: true` schema flag. */
const FEATURED_LIMIT = 3;

export function findInstalledLaunchEntry(app: StoreEntry, installedEntriesBySlug: Map<string, DesktopEntry>): DesktopEntry | undefined {
	const direct = installedEntriesBySlug.get(app.slug);
	if (direct) return direct;
	return Array.from(installedEntriesBySlug.values()).find((entry) => entry.permissionAliases?.includes(app.slug));
}

export function MarketplaceView() {
	const auth = useKhalAuth();
	const fetchApps = useDesktopStore((s) => s.fetchApps);
	const desktopApps = useDesktopStore((s) => s.apps);
	const launchApp = useLaunchApp();

	const [catalog, setCatalog] = useState<StoreEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [installingSet, setInstallingSet] = useState<Set<string>>(new Set());
	const [uninstallingSet, setUninstallingSet] = useState<Set<string>>(new Set());
	const [urlDialogOpen, setUrlDialogOpen] = useState(false);
	const [urlInstallProgress, setUrlInstallProgress] = useState<InstallProgress | null>(null);
	const [query, setQuery] = useState('');

	// Track sandbox provisioning status for the current user
	const sandboxStatus = useSandboxStatus(auth?.userId);

	const installedEntriesBySlug = useMemo(() => new Map(desktopApps.map((entry) => [entry.id, entry])), [desktopApps]);
	const isInstalled = useCallback(
		(app: StoreEntry) => Boolean(findInstalledLaunchEntry(app, installedEntriesBySlug)),
		[installedEntriesBySlug],
	);

	/** Derive sandbox state for a specific app card. */
	const getSandboxState = useCallback(
		(app: StoreEntry): SandboxState | undefined => {
			if (!app.sandboxRequired || !sandboxStatus) return undefined;
			// Only show sandbox state for installed sandbox apps
			if (!isInstalled(app)) return undefined;
			return sandboxStatus.state;
		},
		[sandboxStatus, isInstalled],
	);

	// Fetch store catalog
	useEffect(() => {
		let cancelled = false;
		async function load() {
			try {
				const client = getNatsClient();
				const raw = await client.request('os.apps.store.list', undefined, 8000);
				const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as StoreCatalogResponse;
				if (!cancelled) {
					const items = data.items ?? data.apps ?? [];
					setCatalog(items.filter((app) => !HIDDEN_APP_SLUGS.has(app.slug)));
					setLoading(false);
				}
			} catch (err) {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : String(err));
					setLoading(false);
				}
			}
		}
		load();
		return () => { cancelled = true; };
	}, []);

	// RBAC check: does the user meet the app's minRole?
	const userRole = normalizeRole(auth?.role);
	const isAuthorized = useCallback(
		(app: StoreEntry): boolean => {
			if (!app.minRole) return true;
			return hasMinRole(userRole, normalizeRole(app.minRole) as Role);
		},
		[userRole],
	);

	const handleInstall = useCallback(
		async (slug: string) => {
			setInstallingSet((prev) => new Set(prev).add(slug));
			try {
				const client = getNatsClient();
				// os.apps.register in core triggers sandbox creation if manifest has sandbox: true
				await client.request('os.apps.register', { slug }, 15000);
				// Refresh desktop app list so the new app appears
				await fetchApps();
			} catch (err) {
				console.error(`[Marketplace] install failed for ${slug}:`, err);
			} finally {
				setInstallingSet((prev) => {
					const next = new Set(prev);
					next.delete(slug);
					return next;
				});
			}
		},
		[fetchApps],
	);

	const handleUninstall = useCallback(
		async (slug: string) => {
			if (!window.confirm(`Uninstall ${slug}? This will remove the app and any associated resources.`)) return;
			setUninstallingSet((prev) => new Set(prev).add(slug));
			try {
				const client = getNatsClient();
				// os.apps.unregister in core triggers sandbox deletion if manifest has sandbox: true
				await client.request('os.apps.unregister', { slug }, 15000);
				await fetchApps();
			} catch (err) {
				console.error(`[Marketplace] uninstall failed for ${slug}:`, err);
			} finally {
				setUninstallingSet((prev) => {
					const next = new Set(prev);
					next.delete(slug);
					return next;
				});
			}
		},
		[fetchApps],
	);

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner size="lg" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-2 p-8">
				<p className="text-sm" style={{ color: 'var(--khal-text-secondary)' }}>
					Failed to load app catalog
				</p>
				<p className="text-xs" style={{ color: 'var(--khal-text-muted)' }}>
					{error}
				</p>
			</div>
		);
	}

	// Featured / hero row — heuristic per Decision #10: first FEATURED_LIMIT installed
	// catalog entries. No `featured: true` schema flag is read.
	const featured = catalog.filter(isInstalled).slice(0, FEATURED_LIMIT);
	const featuredSlugs = new Set(featured.map((app) => app.slug));
	const trimmedQuery = query.trim().toLowerCase();
	const matchesQuery = (app: StoreEntry) =>
		!trimmedQuery ||
		app.name.toLowerCase().includes(trimmedQuery) ||
		(app.shortDescription?.toLowerCase().includes(trimmedQuery) ?? false) ||
		app.slug.toLowerCase().includes(trimmedQuery);
	const visibleFeatured = featured.filter(matchesQuery);
	const visibleCatalog = catalog.filter((app) => !featuredSlugs.has(app.slug)).filter(matchesQuery);

	const renderCard = (app: StoreEntry) => (
		<AppCard
			key={app.slug}
			app={app}
			installed={isInstalled(app)}
			launchEntry={findInstalledLaunchEntry(app, installedEntriesBySlug)}
			authorized={isAuthorized(app)}
			installing={installingSet.has(app.slug)}
			uninstalling={uninstallingSet.has(app.slug)}
			sandboxState={getSandboxState(app)}
			onInstall={handleInstall}
			onUninstall={handleUninstall}
			onOpen={launchApp}
		/>
	);

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Integrated header bar: title + url hint + search + install-from-url button */}
			<div
				className="shrink-0 border-b"
				style={{
					borderColor: 'var(--khal-border)',
					padding: '20px 24px 14px',
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<h1
					style={{
						fontFamily: 'var(--khal-font-display)',
						fontWeight: 600,
						fontSize: 22,
						letterSpacing: '-0.01em',
						color: 'var(--khal-fg)',
						margin: 0,
					}}
				>
					Marketplace
				</h1>
				<span
					style={{
						fontFamily: 'var(--khal-font-mono)',
						fontSize: 11.5,
						color: 'var(--khal-muted)',
					}}
				>
					khal://market
				</span>
				<div style={{ flex: 1 }} />
				<input
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search packs…"
					aria-label="Search packs"
					className="k-input"
					style={{ width: 240, height: 28, fontSize: 12 }}
				/>
				<button
					type="button"
					onClick={() => setUrlDialogOpen(true)}
					className="k-btn k-btn-secondary k-btn-sm"
				>
					Install from URL
				</button>
				{urlInstallProgress &&
					urlInstallProgress.phase !== 'succeeded' &&
					urlInstallProgress.phase !== 'failed' && (
						<p
							className="k-helper"
							style={{ width: '100%', marginTop: 4, color: 'var(--khal-muted)' }}
						>
							Installing from URL — {urlInstallProgress.phase}…
						</p>
					)}
				{urlInstallProgress && urlInstallProgress.phase === 'failed' && (
					<p
						className="k-helper"
						style={{ width: '100%', marginTop: 4, color: 'var(--khal-error)' }}
					>
						Install failed{urlInstallProgress.message ? `: ${urlInstallProgress.message}` : ''}
					</p>
				)}
			</div>

			{/* Catalog body: hero/featured row + all packs grid */}
			<div className="flex-1 overflow-y-auto" style={{ padding: 24 }}>
				{catalog.length === 0 ? (
					<div
						className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center"
						data-testid="marketplace-empty-state"
					>
						<p className="text-sm" style={{ color: 'var(--khal-muted)' }}>No apps available</p>
						<p className="text-xs" style={{ color: 'var(--khal-muted)', opacity: 0.7 }}>
							The store catalog (<code>os.apps.store.list</code>) returned no items. If you expect built-in apps here, the core registry may need to be seeded.
						</p>
					</div>
				) : (
					<>
						{visibleFeatured.length > 0 && (
							<section style={{ marginBottom: 24 }} aria-labelledby="market-featured-heading">
								<h2
									id="market-featured-heading"
									className="k-label"
									style={{ marginBottom: 10 }}
								>
									Featured
								</h2>
								<div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4">
									{visibleFeatured.map(renderCard)}
								</div>
							</section>
						)}
						<section aria-labelledby="market-all-heading">
							<h2
								id="market-all-heading"
								className="k-label"
								style={{ marginBottom: 10 }}
							>
								All packs
							</h2>
							<div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
								{visibleCatalog.map(renderCard)}
							</div>
							{visibleCatalog.length === 0 && visibleFeatured.length === 0 && trimmedQuery && (
								<p
									className="k-helper"
									style={{ marginTop: 12, color: 'var(--khal-muted)' }}
								>
									No packs match “{query}”.
								</p>
							)}
						</section>
					</>
				)}
			</div>

			<InstallFromUrlDialog
				open={urlDialogOpen}
				onClose={() => setUrlDialogOpen(false)}
				onProgress={(p) => setUrlInstallProgress(p)}
				onInstalled={() => {
					setUrlInstallProgress(null);
					void fetchApps();
				}}
			/>
		</div>
	);
}
